import { useState, useEffect } from "react";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import Export from "./Export";
import { CSVLink } from "react-csv";

const QueryAnalyzer = () => {
  // Get current UTC time in YYYY-MM-DD HH:MM:SS format
  const formatUTCDateTime = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, "0");
    const day = String(now.getUTCDate()).padStart(2, "0");
    const hours = String(now.getUTCHours()).padStart(2, "0");
    const minutes = String(now.getUTCMinutes()).padStart(2, "0");
    const seconds = String(now.getUTCSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Get current date in YYYY-MM-DD format for date inputs
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  };

  const [filters, setFilters] = useState({
    mau: "",
    category: "",
    indentType: "",
    item: "",
    location: "",
    project: "",
    stage: "",
    status: "",
    fromDate: "",
    toDate: "",
    minQuantity: "",
    maxQuantity: "",
    search: "",
    forceClosure: false,
  });

  const [showOption, setShowOption] = useState("indents");
  const [currentDateTime, setCurrentDateTime] = useState(formatUTCDateTime());
  const [currentUser] = useState("unknown user");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState({
    maus: [],
    categories: [],
    indentTypes: [],
    items: [],
    locations: [],
    projects: [],
    stages: [],
  });

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showOptions = [
    { value: "indents", label: "Indents" },
    { value: "items", label: "Items" },
    { value: "projects", label: "Projects" },
    { value: "categories", label: "Categories" },
    { value: "maus", label: "MAUs" },
  ];

  const statusOptions = [
    { value: "Active", label: "At PE level" },
    { value: "Pending L1 approval", label: "Pending L1 approval" },
    { value: "Pending L2 approval", label: "Pending L2 approval" },
    { value: "Pending PD approval", label: "Pending Purchase Order" },
    { value: "Pending PH approval", label: "Pending Purchase Head approval" },
    { value: "Pending SO approval", label: "Pending Store Officer Approval" },
    { value: "Force Closure Needed", label: "Needed Force Closure" },
    { value: "Closed", label: "Closed Indents" },
    { value: "Pending", label: "Open Items (Pending Receipt)" },
    { value: "Partially Received", label: "Partial Items (Partially Received)" },
    { value: "Completed", label: "Completed Items (Fully Received)" }
  ];

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(formatUTCDateTime());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fetch dropdown data
  const fetchData = async () => {
    try {
      const [
        mausResponse,
        categoriesResponse,
        indentTypesResponse,
        itemsResponse,
        locationsResponse,
        projectsResponse,
        stagesResponse,
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/queryAnalyzer/maus`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/mcategories`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/mindent_types`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/mitems`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/locations`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/mproject`),
        axios.get(`${API_BASE_URL}/queryAnalyzer/mstages`),
      ]);

      setOptions({
        maus: mausResponse.data.items || mausResponse.data,
        categories: categoriesResponse.data.items || categoriesResponse.data,
        indentTypes: indentTypesResponse.data,
        items: itemsResponse.data,
        locations: locationsResponse.data,
        projects: projectsResponse.data,
        stages: stagesResponse.data,
      });
    } catch (error) {
      console.error("Error fetching filter data:", error);
      alert("Failed to fetch filter data. Please try again later.");
    }
  };

  // Fetch filtered results
  const fetchFilteredResults = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("showOption", showOption);

      // Only append filters that have values
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          // Ensure status is properly encoded
          if (key === 'status') {
            params.append(key, encodeURIComponent(value));
          } else {
            params.append(key, value);
          }
        }
      });

      console.log('Query Parameters:', params.toString()); // Debug log

      const response = await axios.get(
        `${API_BASE_URL}/queryAnalyzer/filtered-data?${params.toString()}`
      );
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching filtered results:", error);
      alert("Failed to fetch filtered results. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchFilteredResults();
  };

  const resetFilters = () => {
    setFilters({
      mau: "",
      category: "",
      indentType: "",
      item: "",
      location: "",
      project: "",
      stage: "",
      status: "",
      fromDate: "",
      toDate: "",
      minQuantity: "",
      maxQuantity: "",
      search: "",
      forceClosure: false,
    });
    setShowOption("indents");
    setResults([]);
  };

  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => {
      if (prev.includes(column)) {
        return prev.filter(col => col !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  const handleSelectAllColumns = () => {
    if (results.length > 0) {
      const allColumns = Object.keys(results[0]);
      setSelectedColumns(allColumns);
    }
  };

  const handleDeselectAllColumns = () => {
    setSelectedColumns([]);
  };

  const renderResultsTable = () => {
    if (!results.length) {
      return (
        <div className="bg-gray-50 p-4 rounded-md text-center">
          <p className="text-gray-600">
            No results found for the selected criteria.
          </p>
        </div>
      );
    }

    // Get all available columns from the first result
    const allColumns = Object.keys(results[0]);
    
    // If no columns are selected, show all columns
    const columnsToShow = selectedColumns.length > 0 ? selectedColumns : allColumns;

    return (
      <div className="space-y-4">
        {/* Column Selector Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition flex items-center gap-2"
          >
            <span>Select Columns</span>
            <svg
              className={`w-4 h-4 transform transition-transform ${
                showColumnSelector ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Column Selector Dropdown */}
        {showColumnSelector && (
          <div className="bg-white p-4 rounded-md shadow-md border border-gray-200">
            <div className="flex justify-between mb-4">
              <h4 className="font-medium text-gray-700">Select Columns to Display</h4>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAllColumns}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAllColumns}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {allColumns.map((column) => (
                <label
                  key={column}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column)}
                    onChange={() => handleColumnToggle(column)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {column.replace(/([A-Z])/g, " $1").trim()}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Results Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                {columnsToShow.map((column) => (
                  <th
                    key={column}
                    className="px-6 py-3 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.replace(/([A-Z])/g, " $1").trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                  {columnsToShow.map((column) => (
                    <td
                      key={`${rowIndex}-${column}`}
                      className="px-6 py-4 whitespace-nowrap border-b"
                    >
                      {row[column]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-grow p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white shadow-md rounded-lg p-6">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Query Analyzer
              </h2>
              <div className="text-sm text-gray-600">
                <p className="mb-1">Current User's Login: {currentUser}</p>
                <p>
                  Current Date and Time (UTC - YYYY-MM-DD HH:MM:SS formatted):
                </p>
                <p className="font-medium">{currentDateTime}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Show Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Show Results For
                </label>
                <select
                  value={showOption}
                  onChange={(e) => setShowOption(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {showOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* From Date */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      handleFilterChange("fromDate", e.target.value)
                    }
                    max={filters.toDate || getCurrentDate()}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* To Date */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      handleFilterChange("toDate", e.target.value)
                    }
                    min={filters.fromDate}
                    max={getCurrentDate()}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Search Bar */}
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search across all fields..."
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Filter Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* MAU Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    MAU
                  </label>
                  <select
                    value={filters.mau}
                    onChange={(e) => handleFilterChange("mau", e.target.value)}
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All MAUs</option>
                    {options.maus.map((option) => (
                      <option key={option.UnitID} value={option.UnitID}>
                        {option.UnitName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Categories</option>
                    {options.categories.map((option) => (
                      <option key={option.CategoryID} value={option.CategoryID}>
                        {option.CategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Indent Type Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Indent Type
                  </label>
                  <select
                    value={filters.indentType}
                    onChange={(e) =>
                      handleFilterChange("indentType", e.target.value)
                    }
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Indent Types</option>
                    {options.indentTypes.map((option) => (
                      <option key={option.TypeID} value={option.TypeID}>
                        {option.TypeName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Item
                  </label>
                  <select
                    value={filters.item}
                    onChange={(e) => handleFilterChange("item", e.target.value)}
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Items</option>
                    {options.items.map((option) => (
                      <option key={option.ItemID} value={option.ItemID}>
                        {option.ItemName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <select
                    value={filters.location}
                    onChange={(e) =>
                      handleFilterChange("location", e.target.value)
                    }
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Locations</option>
                    {options.locations.map((option) => (
                      <option key={option.LocationID} value={option.LocationID}>
                        {option.LocationName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Project Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Project
                  </label>
                  <select
                    value={filters.project}
                    onChange={(e) =>
                      handleFilterChange("project", e.target.value)
                    }
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Projects</option>
                    {options.projects.map((option) => (
                      <option key={option.ProjectID} value={option.ProjectID}>
                        {option.ProjectName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Stage Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Stage
                  </label>
                  <select
                    value={filters.stage}
                    onChange={(e) =>
                      handleFilterChange("stage", e.target.value)
                    }
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Stages</option>
                    {options.stages.map((option) => (
                      <option key={option.StageID} value={option.StageID}>
                        {option.StageName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Dropdown */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Statuses</option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity Range */}
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Quantity Range
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={filters.minQuantity}
                      onChange={(e) => handleFilterChange("minQuantity", e.target.value)}
                      placeholder="Min"
                      className="w-1/2 border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="number"
                      value={filters.maxQuantity}
                      onChange={(e) => handleFilterChange("maxQuantity", e.target.value)}
                      placeholder="Max"
                      className="w-1/2 border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Force Closure Checkbox - Only show when items is selected */}
                {showOption === "items" && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="forceClosure"
                      checked={filters.forceClosure}
                      onChange={(e) => handleFilterChange("forceClosure", e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="forceClosure" className="text-sm font-medium text-gray-700">
                      Show Force Closed Items
                    </label>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition"
                >
                  Reset Filters
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Apply Filters"}
                </button>
              </div>
            </form>

            {/* Results Section */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold">Results</h3>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {results.length} {showOption === "indents" ? "Indents" : 
                      showOption === "items" ? "Items" : 
                      showOption === "projects" ? "Projects" : 
                      showOption === "categories" ? "Categories" : 
                      "MAUs"}
                  </span>
                </div>
                {results.length > 0 && (
                  <div className="flex gap-2">
                    <Export
                      results={results}
                      currentDateTime={currentDateTime}
                      selectedColumns={selectedColumns}
                    />
                  </div>
                )}
              </div>
              {loading ? (
                <div className="flex justify-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                renderResultsTable()
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QueryAnalyzer;